import { Component, HostBinding, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, Subject, takeUntil } from "rxjs";
import { LoggingService } from "src/app/core";
import { LocalStorageKeys } from "src/app/core/models/storage-keys";
import { NewFeatureService } from "src/app/core/services/new-feature.service";
import { StorageService } from "src/app/core/services/storage.service";
import { HeaderService } from "src/app/layout/services/header.service";

const Workflow: string = "NewFeatures";
const WorkflowSource: string = "NewFeatureCarouselTrigger";

@Component({
  selector: "app-new-feature-carousel-trigger",
  standalone: false,
  templateUrl: "./new-feature-carousel-trigger.component.html",
  styleUrl: "./new-feature-carousel-trigger.component.scss",
})
export class NewFeatureCarouselTriggerComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();
  private _carouselBlacklistedUrls: string[] = [];

  isExpanded: boolean = false;

  @Input() isMobile: boolean;

  @ViewChild("carouselTemplate", { static: true }) carouselTemplate: TemplateRef<any>;
  @HostBinding("style.display") display: string = "none";

  constructor(
    private _headerService: HeaderService,
    private _storageService: StorageService,
    private _newFeatureService: NewFeatureService,
    private _router: Router,
    private _loggingService: LoggingService,
  ) {}

  ngOnInit(): void {
    this._newFeatureService.lastUpdatedOn$
      .pipe(
        filter((lastUpdatedOn: Date) => !!lastUpdatedOn),
        takeUntil(this._destroyed$),
      )
      .subscribe((lastUpdatedOn: Date) => {
        this._carouselBlacklistedUrls = this._newFeatureService.carouselBlacklistedUrls ?? [];

        if (this._isBlacklistedUrl(this._router.url)) {
          this._hide();
          return;
        } else {
          this.display = "block";
        }

        const drawerHiddenTimestampLocalStorage = this._storageService.local.getInt(LocalStorageKeys.NewFeatureCarouselHidden);
        let drawerHiddenTimestamp: Date = null;

        if (drawerHiddenTimestampLocalStorage) {
          drawerHiddenTimestamp = new Date(drawerHiddenTimestampLocalStorage);
        }

        if (drawerHiddenTimestamp && drawerHiddenTimestamp < lastUpdatedOn) {
          this._storageService.local.remove(LocalStorageKeys.NewFeatureCarouselHidden);
        }

        this._initDrawer();
      });

    this._headerService.headerDrawerToggle$.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      if (this.isExpanded) {
        this._collapseDrawer();
      }
    });

    this._router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this._destroyed$),
      )
      .subscribe({
        next: (e: NavigationEnd) => {
          if (this._isBlacklistedUrl(e.urlAfterRedirects)) {
            this._hide();
          } else {
            this._show();
          }
        },
      });
  }

  ngOnDestroy(): void {
    this._headerService.setHeaderDrawer(null);
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onClick(): void {
    if (this.isExpanded) {
      this._loggingService.logWorkflow(
        "New Feature Carousel Trigger Collapsed",
        "NewFeatureCarouselTriggerComponent.onClick",
        Workflow,
        "NewFeatureCarouselTriggerCollapse",
        WorkflowSource,
      );
      this._collapseDrawer();
    } else {
      this._loggingService.logWorkflow(
        "New Feature Carousel Trigger Expanded",
        "NewFeatureCarouselTriggerComponent.onClick",
        Workflow,
        "NewFeatureCarouselTriggerExpand",
        WorkflowSource,
      );
      this._expandDrawer();
    }
  }

  private _hide(): void {
    this.display = "none";
    this.isExpanded = false;
    this._headerService.setHeaderDrawer(null);
  }

  private _show(): void {
    this.display = "block";
    this._initDrawer();
  }

  private _isBlacklistedUrl(url: string): boolean {
    return !!this._carouselBlacklistedUrls?.some(blacklistedUrl => url.includes(blacklistedUrl));
  }

  private _initDrawer(): void {
    const drawerHidden = this._storageService.local.get(LocalStorageKeys.NewFeatureCarouselHidden);

    if (!drawerHidden) {
      this._expandDrawer();
    }
  }

  private _expandDrawer(): void {
    this.isExpanded = true;
    this._headerService.setHeaderDrawer(this.carouselTemplate);
    this._storageService.local.remove(LocalStorageKeys.NewFeatureCarouselHidden);
  }

  private _collapseDrawer(): void {
    this.isExpanded = false;
    this._headerService.setHeaderDrawer(null);
    this._storageService.local.set(LocalStorageKeys.NewFeatureCarouselHidden, new Date().getTime().toString());
  }
}
